import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
    FaDatabase, FaPlus, FaSyncAlt, FaPlug, FaGlobe, FaListUl, FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
    FaArrowLeft, FaArrowRight, FaSave, FaPlay, FaMagic, FaHistory, FaEye, FaCode, FaTrash, FaSlidersH,
} from 'react-icons/fa';
import ConnectorStudio from './ConnectorStudio';
import ConnectorAssistant from './ConnectorAssistant';
import TicketWorkflow from './TicketWorkflow';
import TicketJourney from './dataSources/TicketJourney';
import SourcesByCategory, { CATEGORIES } from './dataSources/SourcesByCategory';
import './DataSources.css';

// Data sources (docs/data-sources-v2-spec.md §4–5, docs/ticket-journey.md):
// the guided page. Home = the ticket journey (each step, what the account has
// set up, the AI steps) then the sources grouped by kind of system with the
// moment each is read; three creation paths (API in five steps, web portal,
// catalogue in one step), the source's page (steps revisitable, history,
// verification period, expert mode = the connector studio editor) and the
// enrichment settings (ticket workflow).

const API = '/api/integrations/connectors';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const SECRET_FIELDS = ['password', 'token', 'value', 'client_secret', 'api_key'];
const AUTH_CHOICES = [
    { type: 'header', fields: ['header_name', 'value'] },
    { type: 'basic', fields: ['username', 'password'] },
    { type: 'bearer', fields: ['token'] },
    { type: 'oauth2', fields: ['token_url', 'client_id', 'client_secret', 'scope'] },
    { type: 'none', fields: [] },
];
const IDENTIFIERS = ['order_number', 'tracking_number', 'customer_email', 'invoice_number', 'client_ref'];
// The fields the guided « champs récupérés » step shows, per section.
const FIELD_SECTIONS = [
    { section: 'order', list: false, fields: ['number', 'status', 'created_at', 'shipped_at', 'customer_name', 'customer_email', 'client_name', 'shipping_method', 'warehouse', 'total', 'notes'] },
    { section: 'shipments', list: true, fields: ['tracking_number', 'carrier', 'service', 'status', 'shipped_at', 'delivered_at', 'weight'] },
    { section: 'events', list: true, fields: ['date', 'status', 'location', 'code'] },
    { section: 'stock_lines', list: true, fields: ['sku', 'designation', 'barcode', 'quantity_ordered', 'quantity_shipped', 'quantity_available', 'state'] },
    { section: 'recipient_address', list: false, fields: ['firstname', 'lastname', 'company', 'address1', 'address2', 'postcode', 'city', 'country', 'phone', 'email', 'raw'] },
];

const slug = (text) => String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
const exprText = (expr) => (typeof expr === 'string' ? expr : expr === undefined || expr === null ? '' : JSON.stringify(expr));
const detectKind = (text) => {
    const trimmed = (text || '').trim();
    if (/^curl\b/i.test(trimmed)) return 'curl';
    if (/"openapi"\s*:|"swagger"\s*:|^openapi:|^swagger:|"paths"\s*:|\npaths:/i.test(trimmed)) return 'openapi';
    return 'sample';
};
const emptySpec = () => ({
    spec_version: 1, title: '', transport: 'http_json', inputs: ['order_number'], require_any: ['order_number'], params: {},
    steps: [{ id: 'lookup', request: { method: 'GET', url: '/orders/{order_number}', query: {}, headers: { Accept: 'application/json' }, timeout_s: 30 },
        response: { format: 'json', data_path: '$', not_found_on_404: true }, on_not_found: 'stop', role: 'result' }],
    mapping: {}, modes: ['enrich_on_ingest'], operations: { timeout_s: 30, retries: 1, health_alerts: true }, presentation: { path: 'api', step: 1 },
});
const newDraftId = () => `draft_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const emptyDraft = (category = null) => ({ key: '', base_url: '', auth: { type: 'header', header_name: 'X-Api-Key', value: '' }, enabled: true, spec: { ...emptySpec(), ...(category ? { category } : {}), presentation: { path: 'api', step: 1, draft_id: newDraftId() } }, sample: null, saved: false });
const canEdit = () => {
    try {
        return JSON.parse(localStorage.getItem('userData') || '{}').role !== 'employee';
    } catch (e) {
        return true;
    }
};

// « Type de système » select of the creation steps (spec.category).
const CategorySelect = ({ t, value, onChange }) => (
    <label className="ds-field"><span>{t('dataSources.step1.category', 'Type de système')}</span>
        <select className="ds-input" value={value || ''} onChange={(e) => onChange(e.target.value || null)}>
            <option value="">{t('dataSources.step1.categoryAuto', 'Déduit des champs lus')}</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{t(`dataSources.category.${c.key}.title`, c.title)}</option>)}
        </select>
    </label>
);

// Apply the dotted patch of an assistant proposal to a draft (whitelist mirrors the backend's).
const applyAssistantPatch = (draft, patch) => {
    const next = JSON.parse(JSON.stringify(draft));
    Object.entries(patch || {}).forEach(([path, value]) => {
        if (path === 'auth') { next.auth = { ...value }; return; }
        const parts = path.split('.');
        let cursor = next;
        for (let i = 0; i < parts.length - 1; i += 1) {
            const part = parts[i];
            const key = /^\d+$/.test(part) ? Number(part) : part;
            if (cursor[key] === undefined || cursor[key] === null) cursor[key] = /^\d+$/.test(parts[i + 1]) ? [] : {};
            cursor = cursor[key];
        }
        const last = parts[parts.length - 1];
        cursor[/^\d+$/.test(last) ? Number(last) : last] = value;
    });
    return next;
};

const VIEWS_FROM_URL = ['choose', 'workflow'];

const DataSources = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [listing, setListing] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [journey, setJourney] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [view, setView] = useState(() => {
        const wanted = searchParams.get('view');
        // ?view=choose&category=warehouse opens the creation paths for that kind of system (onboarding).
        const category = CATEGORIES.some((c) => c.key === searchParams.get('category')) ? searchParams.get('category') : undefined;
        return VIEWS_FROM_URL.includes(wanted) ? { mode: wanted, category } : { mode: 'home' };
    });
    const editable = canEdit();
    // ?step=<journey step> opens that step (onboarding, copilot, « Configurer » links).
    const [initialStep] = useState(() => searchParams.get('step'));

    // A deep link (« Configurer » of the journey, the old workflow tab, a
    // journey step) is consumed once: going home must not reopen it.
    useEffect(() => {
        if (searchParams.get('view') || searchParams.get('step')) {
            const next = new URLSearchParams(searchParams);
            next.delete('view');
            next.delete('step');
            next.delete('category');
            setSearchParams(next, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadJourney = useCallback(async () => {
        try {
            const res = await axios.get('/api/setup/journey', authHeaders());
            setJourney(res.data);
        } catch (e) {
            setJourney(null);
        }
    }, []);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [list, cat] = await Promise.all([axios.get(API, authHeaders()), axios.get(`${API}/catalog`, authHeaders())]);
            setListing(list.data);
            setCatalog(cat.data.entries || []);
        } catch (e) {
            setError(t('dataSources.loadError', 'Impossible de charger les sources de données.'));
        }
        await loadJourney();
    }, [t, loadJourney]);

    useEffect(() => { load(); }, [load]);

    const goHome = async (message) => { await load(); setView({ mode: 'home' }); if (message) setNotice(message); };

    if (!listing) {
        return <div className="ds-page"><p className="ds-muted">{error || t('dataSources.loading', 'Chargement…')}</p></div>;
    }

    return (
        <div className="ds-page">
            <div className="ds-head">
                <h1><FaDatabase /> {t('dataSources.title', 'Sources de données')}</h1>
                <p className="ds-muted">{t('dataSources.introJourney', 'Vos systèmes — ERP, boutiques, WMS, TMS, API de suivi — complètent chaque ticket avec la commande, les colis et le suivi. Le parcours ci-dessous montre à quelle étape chacun intervient et ce qui manque encore.')}</p>
            </div>
            {error && <div className="ds-alert ds-alert-error"><FaTimesCircle /> {error}</div>}
            {notice && <div className="ds-alert ds-alert-ok"><FaCheckCircle /> {notice} <button type="button" className="ds-link" onClick={() => setNotice(null)}>×</button></div>}

            {view.mode === 'home' && (
                <div className="ds-home">
                    {journey && <TicketJourney journey={journey} canEdit={editable} onChanged={loadJourney} initialStep={initialStep} />}
                    <div className="ds-sources-head">
                        <div>
                            <h2>{t('dataSources.yourSources', 'Vos sources')}</h2>
                            <p className="ds-muted">{t('dataSources.yourSourcesHint', 'Classées par type de système, avec le moment où chacune est lue et ce avec quoi elle est interrogée.')}</p>
                        </div>
                        <div className="ds-actions">
                            {editable && <button type="button" className="u-btn u-btn--primary" onClick={() => setView({ mode: 'choose' })}><FaPlus /> {t('dataSources.add', 'Ajouter une source')}</button>}
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setView({ mode: 'workflow' })}><FaSlidersH /> {t('dataSources.workflowSettings', 'Réglages de l’enrichissement')}</button>
                            <button type="button" className="u-btn u-btn--secondary" onClick={load} aria-label={t('dataSources.reload', 'Actualiser')}><FaSyncAlt /></button>
                        </div>
                    </div>
                    {journey ? (
                        <SourcesByCategory sources={journey.sources} connectors={listing.connectors} canEdit={editable}
                            onAdd={(category) => setView({ mode: 'choose', category })}
                            onOpen={(key) => setView({ mode: 'source', key })}
                            onUpgraded={(key) => goHome(t('dataSources.upgraded', 'Source mise à niveau : « {{key}} ». Vérifiez-la avec une vraie commande.', { key }))} />
                    ) : (
                        <p className="ds-muted">{t('dataSources.journeyUnavailable', 'Le parcours n’a pas pu être chargé.')}</p>
                    )}
                </div>
            )}
            {view.mode === 'workflow' && (
                <div className="ds-card">
                    <button type="button" className="ds-link" onClick={() => goHome()}><FaArrowLeft /> {t('dataSources.back', 'Retour')}</button>
                    <TicketWorkflow onFeedback={(feedback) => feedback && setNotice(feedback.message || feedback.text || null)} />
                </div>
            )}
            {view.mode === 'choose' && <ChoosePath t={t} category={view.category} onBack={() => setView({ mode: 'home' })} onApi={() => setView({ mode: 'api', category: view.category })} onWeb={() => setView({ mode: 'web', category: view.category })} onCatalog={() => setView({ mode: 'catalog' })} />}
            {view.mode === 'web' && <WebWizard t={t} catalog={catalog} category={view.category} onBack={() => setView({ mode: 'choose', category: view.category })} onDone={(key) => goHome(t('dataSources.created', 'Source « {{key}} » enregistrée et active.', { key }))} />}
            {view.mode === 'api' && <ApiWizard t={t} category={view.category} onBack={() => setView({ mode: 'choose', category: view.category })} onDone={(key) => goHome(t('dataSources.created', 'Source « {{key}} » enregistrée et active.', { key }))} />}
            {view.mode === 'catalog' && <CatalogInstall t={t} entries={catalog} onBack={() => setView({ mode: 'choose' })} onDone={(key) => goHome(t('dataSources.created', 'Source « {{key}} » enregistrée et active.', { key }))} />}
            {view.mode === 'source' && <SourcePage t={t} keyName={view.key} onBack={() => goHome()} onDeleted={() => goHome(t('dataSources.deleted', 'Source supprimée.'))} />}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------

const stateOf = (source, t) => {
    if (source.legacy) return { cls: 'legacy', label: t('dataSources.state.legacy', 'héritée') };
    if (source.enabled === false) return { cls: 'off', label: t('dataSources.state.off', 'désactivée') };
    if (source.health?.status === 'failing') return { cls: 'bad', label: t('dataSources.state.failing', 'en échec depuis {{since}}', { since: source.health.first_failure_at ? new Date(source.health.first_failure_at).toLocaleString() : '?' }) };
    if (source.shadow_of) return { cls: 'verify', label: t('dataSources.state.verifying', 'en vérification') };
    return { cls: 'ok', label: t('dataSources.state.active', 'active') };
};

const systemLabel = (source, t) => {
    const system = source.presentation?.system || source.source?.ref || source.source?.kind;
    if (source.brand_id) return t('dataSources.system.brand', 'API de suivi de marque');
    if (system === 'effitrace' || source.connector_type === 'effitrace') return 'EffiTrace';
    if (source.connector_type === 'ship24') return 'Ship24';
    return { http_json: t('dataSources.system.api', 'API'), http_form: t('dataSources.system.web', 'Site web'), soap: 'SOAP', browser_recipe: t('dataSources.system.portal', 'Portail (parcours)') }[source.transport] || source.transport || '—';
};

// ---------------------------------------------------------------------------
// Path choice
// ---------------------------------------------------------------------------

const ChoosePath = ({ t, category, onBack, onApi, onWeb, onCatalog }) => (
    <div className="ds-card">
        <button type="button" className="ds-link" onClick={onBack}><FaArrowLeft /> {t('dataSources.back', 'Retour')}</button>
        <h2>{t('dataSources.choose.title', 'Comment accède-t-on à ce système ?')}</h2>
        {category && (
            <p className="ds-muted">{t('dataSources.choose.forCategory', 'Nouvelle source : {{category}}', {
                category: t(`dataSources.category.${category}.title`, (CATEGORIES.find((c) => c.key === category) || {}).title || category),
            })}</p>
        )}
        <div className="ds-choices">
            <button type="button" className="ds-choice" onClick={onApi}>
                <FaPlug /><strong>{t('dataSources.choose.api', 'Il a une API')}</strong>
                <span>{t('dataSources.choose.apiDesc', 'Une adresse, une clé ou un identifiant, et la documentation ou un exemple de réponse.')}</span>
            </button>
            <button type="button" className="ds-choice" onClick={onWeb}>
                <FaGlobe /><strong>{t('dataSources.choose.web', 'Il a un site web sur lequel je me connecte')}</strong>
                <span>{t('dataSources.choose.webDesc', 'Vous montrez une fois comment vous trouvez une commande ; le système la relit ensuite à chaque ticket.')}</span>
            </button>
            <button type="button" className="ds-choice" onClick={onCatalog}>
                <FaListUl /><strong>{t('dataSources.choose.catalog', 'Il est dans la liste')}</strong>
                <span>{t('dataSources.choose.catalogDesc', 'EffiTrace, Spacefill, Ship24, les API de suivi de vos marques : une seule étape.')}</span>
            </button>
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// Path A — API, five steps
// ---------------------------------------------------------------------------

const STEPS = ['name', 'connection', 'search', 'fields', 'verify'];

const ApiWizard = ({ t, onBack, onDone, initial = null, onSaved = null, category = null }) => {
    const [draft, setDraft] = useState(initial || emptyDraft(category));
    const [step, setStep] = useState(initial?.spec?.presentation?.step && initial.saved ? 5 : 1);
    const [busy, setBusy] = useState(null);
    const [message, setMessage] = useState(null);
    const [docText, setDocText] = useState('');
    const [connection, setConnection] = useState(null);
    const [evaluation, setEvaluation] = useState(null);
    const [selectedField, setSelectedField] = useState(null);
    const [verification, setVerification] = useState(null);
    const [identifiers, setIdentifiers] = useState({});
    const [lastTest, setLastTest] = useState(null);

    const spec = draft.spec;
    const draftId = draft.saved ? draft.key : (spec.presentation?.draft_id || null);
    const applyFromAssistant = (patch, sample) => {
        setDraft((d) => {
            const next = applyAssistantPatch(d, patch);
            if (sample !== null && sample !== undefined) next.sample = sample;
            return next;
        });
        setMessage({ type: 'ok', text: t('dataSources.assistantApplied', 'Proposition appliquée : vérifiez les étapes concernées.') });
    };
    const request = spec.steps[0].request;
    const response = spec.steps[0].response;
    const setSpec = (patch) => setDraft((d) => ({ ...d, spec: { ...d.spec, ...patch } }));
    const setRequest = (patch) => setSpec({ steps: [{ ...spec.steps[0], request: { ...request, ...patch } }] });
    const setResponse = (patch) => setSpec({ steps: [{ ...spec.steps[0], response: { ...response, ...patch } }] });
    const fail = (e, fallback) => { const d = e?.response?.data?.detail; setMessage({ type: 'error', text: typeof d === 'string' ? d : fallback }); };

    const analyse = async () => {
        if (!docText.trim()) return;
        setBusy('analyse');
        setMessage(null);
        try {
            const kind = detectKind(docText);
            const payload = kind === 'sample' ? { sample: docText, base_url: draft.base_url, request, title: spec.title } : { text: docText, title: spec.title };
            const res = await axios.post(`${API}/propose`, { kind, payload, use_model: true }, authHeaders());
            const proposal = res.data;
            if (kind === 'sample') {
                setDraft((d) => ({ ...d, sample: proposal.sample, spec: { ...d.spec, mapping: proposal.spec.mapping, steps: [{ ...d.spec.steps[0], response: { ...d.spec.steps[0].response, data_path: proposal.spec.steps[0].response.data_path } }] } }));
            } else {
                setDraft((d) => ({
                    ...d, base_url: proposal.base_url || d.base_url, auth: proposal.auth?.type ? proposal.auth : d.auth, sample: proposal.sample || d.sample,
                    spec: { ...d.spec, title: d.spec.title || proposal.spec.title, inputs: proposal.spec.inputs?.length ? proposal.spec.inputs : d.spec.inputs,
                        require_any: proposal.spec.require_any?.length ? proposal.spec.require_any : d.spec.require_any, steps: proposal.spec.steps, mapping: proposal.spec.mapping || d.spec.mapping,
                        tool: proposal.spec.tool || d.spec.tool },
                }));
            }
            setMessage({ type: 'ok', text: [t('dataSources.analysed', 'Documentation lue : les étapes suivantes sont pré-remplies.'), ...(proposal.warnings || [])].join(' ') });
        } catch (e) {
            fail(e, t('dataSources.analyseError', 'La documentation n’a pas pu être lue.'));
        } finally {
            setBusy(null);
        }
    };

    const testConnection = async () => {
        setBusy('connection');
        setConnection(null);
        try {
            const res = await axios.post(`${API}/test-connection`, { key: draft.saved ? draft.key : null, base_url: draft.base_url, auth: draft.auth, spec }, authHeaders());
            setConnection(res.data);
            setLastTest({ ...res.data, stamp: Date.now(), identifiers: {} });
        } catch (e) {
            fail(e, t('dataSources.testError', 'Le test a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const evaluate = useCallback(async (mapping, sample) => {
        if (sample === null || sample === undefined) { setEvaluation(null); return; }
        try {
            const res = await axios.post(`${API}/evaluate`, { mapping, sample, data_path: response.data_path }, authHeaders());
            setEvaluation(res.data);
        } catch (e) { setEvaluation(null); }
    }, [response.data_path]);

    useEffect(() => { if (step === 4) evaluate(spec.mapping, draft.sample); }, [step, spec.mapping, draft.sample, evaluate]);

    const readRealOrder = async () => {
        setBusy('read');
        setMessage(null);
        try {
            const res = await axios.post(`${API}/dry-run`, { key: draft.saved ? draft.key : null, spec, base_url: draft.base_url, auth: draft.auth, identifiers }, authHeaders());
            if (res.data.raw && typeof res.data.raw === 'object' && !res.data.raw._truncated) {
                // the raw data comes after data_path: keep the mapping relative to it
                setDraft((d) => ({ ...d, sample: res.data.raw, spec: { ...d.spec, steps: [{ ...d.spec.steps[0], response: { ...d.spec.steps[0].response, data_path: '$' } }] } }));
                setMessage({ type: 'ok', text: t('dataSources.readOk', 'Réponse reçue : affectez les champs ci-dessous.') });
            } else {
                setMessage({ type: 'error', text: `${res.data.reason}${res.data.error ? ` — ${res.data.error}` : ''}` });
                setLastTest({ ok: false, found: false, reason: res.data.reason, error: res.data.error, raw: res.data.raw, steps: res.data.steps, identifiers, stamp: Date.now() });
            }
        } catch (e) {
            fail(e, t('dataSources.readError', 'La lecture a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const proposeFromSample = async () => {
        if (!draft.sample) return;
        setBusy('propose');
        try {
            const res = await axios.post(`${API}/propose`, { kind: 'sample', payload: { sample: draft.sample, base_url: draft.base_url, request, title: spec.title }, use_model: true }, authHeaders());
            setSpec({ mapping: res.data.spec.mapping });
            setResponse({ data_path: res.data.spec.steps[0].response.data_path });
            setMessage({ type: 'ok', text: t('dataSources.proposed', 'Champs proposés : vérifiez-les et corrigez d’un clic.') });
        } catch (e) {
            fail(e, t('dataSources.analyseError', 'La documentation n’a pas pu être lue.'));
        } finally {
            setBusy(null);
        }
    };

    const assign = (row) => {
        if (!selectedField) return;
        const { section, field } = selectedField;
        const meta = FIELD_SECTIONS.find((s) => s.section === section);
        const mapping = JSON.parse(JSON.stringify(spec.mapping || {}));
        if (meta.list) {
            if (field === '__each') {
                mapping[section] = { each: row.path, fields: mapping[section]?.fields || {} };
            } else {
                const each = mapping[section]?.each;
                let relative = row.path;
                if (each && row.path.startsWith(each)) relative = '$' + row.path.slice(each.length).replace(/^\[\d+\]/, '');
                mapping[section] = { each: each || '', fields: { ...(mapping[section]?.fields || {}), [field]: relative } };
            }
        } else {
            mapping[section] = { ...(mapping[section] || {}), [field]: row.path };
        }
        setSpec({ mapping });
    };

    const clearField = (section, field) => {
        const mapping = JSON.parse(JSON.stringify(spec.mapping || {}));
        const meta = FIELD_SECTIONS.find((s) => s.section === section);
        if (meta.list) { if (mapping[section]?.fields) delete mapping[section].fields[field]; } else if (mapping[section]) delete mapping[section][field];
        setSpec({ mapping });
    };

    const verify = async () => {
        setBusy('verify');
        setVerification(null);
        try {
            const res = await axios.post(`${API}/dry-run`, { key: draft.saved ? draft.key : null, spec, base_url: draft.base_url, auth: draft.auth, identifiers }, authHeaders());
            setVerification(res.data);
            setLastTest({ ok: Boolean(res.data.found), found: res.data.found, reason: res.data.reason, error: res.data.error, raw: res.data.raw, steps: res.data.steps, identifiers, stamp: Date.now() });
        } catch (e) {
            fail(e, t('dataSources.readError', 'La lecture a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const save = async () => {
        const key = draft.key || slug(spec.title);
        if (!key) { setMessage({ type: 'error', text: t('dataSources.nameRequired', 'Donnez un nom à la source.') }); return; }
        setBusy('save');
        setMessage(null);
        try {
            const toSave = { ...spec, presentation: { ...(spec.presentation || {}), path: 'api', step }, tool: spec.modes.includes('ai_tool') ? (spec.tool?.name ? spec.tool : { name: `lookup_${key}`, description: `Look up an order in ${spec.title || key}` }) : undefined };
            const res = await axios.put(`${API}/${encodeURIComponent(key)}`, { spec: toSave, base_url: draft.base_url, auth: draft.auth, enabled: draft.enabled }, authHeaders());
            setDraft((d) => ({ ...d, key, saved: true, auth: res.data.auth, spec: res.data.spec }));
            if (onSaved) onSaved(res.data); else onDone(key);
        } catch (e) {
            fail(e, t('dataSources.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const canNext = { 1: Boolean(spec.title), 2: Boolean(draft.base_url), 3: spec.inputs.length > 0 && Boolean(request.url), 4: true, 5: true }[step];
    const authChoice = AUTH_CHOICES.find((c) => c.type === draft.auth?.type) || AUTH_CHOICES[0];

    return (
        <div className="ds-card ds-with-assistant">
          <div className="ds-wizard-main">
            <div className="ds-wizard-head">
                <button type="button" className="ds-link" onClick={onBack}><FaArrowLeft /> {t('dataSources.back', 'Retour')}</button>
                <ol className="ds-stepper">
                    {STEPS.map((name, i) => (
                        <li key={name} className={step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''} onClick={() => (draft.saved || step > i + 1) && setStep(i + 1)}>
                            <span>{i + 1}</span> {t(`dataSources.steps.${name}`, name)}
                        </li>
                    ))}
                </ol>
            </div>
            {message && <div className={`ds-alert ${message.type === 'ok' ? 'ds-alert-ok' : 'ds-alert-error'}`}>{message.type === 'ok' ? <FaCheckCircle /> : <FaTimesCircle />} {message.text}</div>}

            {step === 1 && (
                <div className="ds-step">
                    <h2>{t('dataSources.step1.title', 'Nom et documentation')}</h2>
                    <label className="ds-field"><span>{t('dataSources.step1.name', 'Nom de la source')}</span>
                        <input className="ds-input" value={spec.title} onChange={(e) => { setSpec({ title: e.target.value }); if (!draft.saved) setDraft((d) => ({ ...d, key: slug(e.target.value) })); }} placeholder={t('dataSources.step1.namePlaceholder', 'ex. WMS Acme')} /></label>
                    <CategorySelect t={t} value={spec.category} onChange={(value) => setSpec({ category: value || undefined })} />
                    <label className="ds-field"><span>{t('dataSources.step1.docs', 'Documentation (facultatif) : collez un fichier OpenAPI, une commande cURL ou une réponse d’exemple')}</span>
                        <textarea className="ds-textarea" value={docText} onChange={(e) => setDocText(e.target.value)} spellCheck={false} placeholder={t('dataSources.step1.docsPlaceholder', 'Le système lit ce que vous collez et pré-remplit la connexion, la recherche et les champs.')} /></label>
                    <button type="button" className="ds-btn" disabled={busy === 'analyse' || !docText.trim()} onClick={analyse}><FaMagic /> {t('dataSources.step1.analyse', 'Lire la documentation')}</button>
                </div>
            )}

            {step === 2 && (
                <div className="ds-step">
                    <h2>{t('dataSources.step2.title', 'Connexion')}</h2>
                    <label className="ds-field"><span>{t('dataSources.step2.baseUrl', 'Adresse de l’API')}</span>
                        <input className="ds-input" value={draft.base_url} onChange={(e) => setDraft((d) => ({ ...d, base_url: e.target.value }))} placeholder="https://wms.example.com/api" /></label>
                    <label className="ds-field"><span>{t('dataSources.step2.authType', 'Type d’accès')}</span>
                        <select className="ds-input" value={draft.auth?.type || 'none'} onChange={(e) => setDraft((d) => ({ ...d, auth: { type: e.target.value, ...(e.target.value === 'header' ? { header_name: 'X-Api-Key' } : {}) } }))}>
                            {AUTH_CHOICES.map((c) => <option key={c.type} value={c.type}>{t(`dataSources.auth.${c.type}`, c.type)}</option>)}
                        </select></label>
                    {authChoice.fields.map((field) => (
                        <label key={field} className="ds-field"><span>{t(`dataSources.authField.${field}`, field)}</span>
                            <input className="ds-input" type={SECRET_FIELDS.includes(field) ? 'password' : 'text'} value={draft.auth?.[field] || ''} placeholder={SECRET_FIELDS.includes(field) ? '••••••••' : ''}
                                onChange={(e) => setDraft((d) => ({ ...d, auth: { ...d.auth, [field]: e.target.value } }))} /></label>
                    ))}
                    <button type="button" className="ds-btn" disabled={busy === 'connection' || !draft.base_url} onClick={testConnection}><FaPlay /> {t('dataSources.step2.test', 'Tester la connexion')}</button>
                    {connection && <div className={`ds-alert ${connection.ok ? 'ds-alert-ok' : 'ds-alert-error'}`}>{connection.ok ? <FaCheckCircle /> : <FaTimesCircle />} {connection.explanation}{connection.error && !connection.ok ? ` (${connection.error})` : ''}</div>}
                </div>
            )}

            {step === 3 && (
                <div className="ds-step">
                    <h2>{t('dataSources.step3.title', 'Recherche')}</h2>
                    <p className="ds-muted">{t('dataSources.step3.with', 'Avec quoi cherchez-vous une commande ?')}</p>
                    <div className="ds-chips">
                        {IDENTIFIERS.map((name) => (
                            <label key={name} className={`ds-chip ${spec.inputs.includes(name) ? 'on' : ''}`}>
                                <input type="checkbox" checked={spec.inputs.includes(name)} onChange={() => { const inputs = spec.inputs.includes(name) ? spec.inputs.filter((v) => v !== name) : [...spec.inputs, name]; setSpec({ inputs, require_any: inputs }); }} />
                                {t(`ticketWorkflow.identifier.${name}`, name)}
                            </label>
                        ))}
                    </div>
                    <div className="ds-row">
                        <label className="ds-field ds-short"><span>{t('dataSources.step3.method', 'Méthode')}</span>
                            <select className="ds-input" value={request.method} onChange={(e) => setRequest({ method: e.target.value })}>{['GET', 'POST'].map((m) => <option key={m}>{m}</option>)}</select></label>
                        <label className="ds-field ds-grow"><span>{t('dataSources.step3.path', 'Chemin de la recherche')}</span>
                            <input className="ds-input ds-mono" value={request.url} onChange={(e) => setRequest({ url: e.target.value })} placeholder="/orders/{order_number}" /></label>
                    </div>
                    <p className="ds-muted">{t('dataSources.step3.hint', 'Écrivez un identifiant entre accolades là où le système l’attend : {order_number}, {tracking_number}, {customer_email}…')}</p>
                    <ParamsEditor t={t} label={t('dataSources.step3.query', 'Paramètres de l’adresse (?clé=valeur)')} value={request.query || {}} onChange={(query) => setRequest({ query })} />
                    {request.method === 'POST' && <ParamsEditor t={t} label={t('dataSources.step3.body', 'Champs envoyés (JSON)')} value={request.body || {}} onChange={(body) => setRequest({ body })} />}
                    <label className="ds-field"><span>{t('dataSources.step3.dataPath', 'Où se trouve la commande dans la réponse ? (laissez $ si c’est toute la réponse)')}</span>
                        <input className="ds-input ds-mono ds-short" value={response.data_path || '$'} onChange={(e) => setResponse({ data_path: e.target.value })} /></label>
                </div>
            )}

            {step === 4 && (
                <div className="ds-step">
                    <h2>{t('dataSources.step4.title', 'Champs récupérés')}</h2>
                    <div className="ds-row">
                        <div className="ds-grow">
                            <p className="ds-muted">{t('dataSources.step4.hint', 'Collez un exemple de réponse, ou lisez une vraie commande. Puis sélectionnez un champ à droite et cliquez sa valeur à gauche.')}</p>
                            <div className="ds-row">
                                {spec.inputs.map((name) => <input key={name} className="ds-input ds-short" placeholder={t(`ticketWorkflow.identifier.${name}`, name)} value={identifiers[name] || ''} onChange={(e) => setIdentifiers({ ...identifiers, [name]: e.target.value })} />)}
                                <button type="button" className="ds-btn" disabled={busy === 'read' || !draft.base_url} onClick={readRealOrder}><FaPlay /> {t('dataSources.step4.read', 'Lire une vraie commande')}</button>
                                <button type="button" className="ds-btn" disabled={busy === 'propose' || !draft.sample} onClick={proposeFromSample}><FaMagic /> {t('dataSources.step4.propose', 'Proposer automatiquement')}</button>
                            </div>
                        </div>
                    </div>
                    <div className="ds-split">
                        <div className="ds-pane">
                            <textarea className="ds-textarea ds-sample" placeholder={t('dataSources.step4.samplePlaceholder', 'Collez ici un exemple de réponse (JSON)')} spellCheck={false}
                                value={typeof draft.sample === 'string' ? draft.sample : draft.sample ? JSON.stringify(draft.sample, null, 2) : ''}
                                onChange={(e) => { const text = e.target.value; try { setDraft((d) => ({ ...d, sample: JSON.parse(text) })); } catch (err) { setDraft((d) => ({ ...d, sample: text })); } }} />
                            {evaluation?.rows && (
                                <div className="ds-rows">
                                    {evaluation.rows.map((row) => (
                                        <button type="button" key={row.path} className={`ds-rowbtn ${row.kind}`} disabled={!selectedField} onClick={() => assign(row)} title={row.path}>
                                            <code>{row.path.replace(/^\$\.?/, '')}</code><span>{String(row.value ?? '∅')}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="ds-pane">
                            {FIELD_SECTIONS.map((meta) => (
                                <details key={meta.section} open={meta.section === 'order' || meta.section === 'shipments'} className="ds-section">
                                    <summary>{t(`connectorStudio.section_${meta.section}`, meta.section)}</summary>
                                    {meta.list && (
                                        <FieldRow t={t} label={t('dataSources.step4.each', 'Liste')} expr={spec.mapping?.[meta.section]?.each} value={evaluation?.canonical?.[meta.section]?.length !== undefined ? `${evaluation.canonical[meta.section].length} ${t('dataSources.step4.items', 'éléments')}` : null}
                                            selected={selectedField?.section === meta.section && selectedField.field === '__each'} onSelect={() => setSelectedField({ section: meta.section, field: '__each' })} onClear={null} />
                                    )}
                                    {meta.fields.map((field) => {
                                        const expr = meta.list ? spec.mapping?.[meta.section]?.fields?.[field] : spec.mapping?.[meta.section]?.[field];
                                        const value = meta.list ? evaluation?.canonical?.[meta.section]?.[0]?.[field] : evaluation?.canonical?.[meta.section]?.[field];
                                        return <FieldRow key={field} t={t} label={t(`dataSources.field.${meta.section}.${field}`, field)} expr={expr} value={value}
                                            selected={selectedField?.section === meta.section && selectedField.field === field} onSelect={() => setSelectedField({ section: meta.section, field })} onClear={expr ? () => clearField(meta.section, field) : null} />;
                                    })}
                                </details>
                            ))}
                            {evaluation?.errors?.length > 0 && <div className="ds-alert ds-alert-error">{evaluation.errors.join(' · ')}</div>}
                        </div>
                    </div>
                </div>
            )}

            {step === 5 && (
                <div className="ds-step">
                    <h2>{t('dataSources.step5.title', 'Vérification et usage')}</h2>
                    <div className="ds-row">
                        {spec.inputs.map((name) => <input key={name} className="ds-input ds-short" placeholder={t(`ticketWorkflow.identifier.${name}`, name)} value={identifiers[name] || ''} onChange={(e) => setIdentifiers({ ...identifiers, [name]: e.target.value })} />)}
                        <button type="button" className="ds-btn" disabled={busy === 'verify'} onClick={verify}><FaPlay /> {t('dataSources.step5.verify', 'Vérifier avec cette commande')}</button>
                    </div>
                    {verification && (
                        <div className={`ds-alert ${verification.found ? 'ds-alert-ok' : 'ds-alert-error'}`}>
                            {verification.found ? <FaCheckCircle /> : <FaExclamationTriangle />} {verification.found ? t('dataSources.step5.found', 'Commande trouvée en {{ms}} ms.', { ms: verification.duration_ms }) : `${verification.reason}${verification.error ? ` — ${verification.error}` : ''}`}
                        </div>
                    )}
                    {verification?.prompt_preview && (
                        <div className="ds-preview"><h4>{t('dataSources.step5.preview', 'Ce que l’agent lira')}</h4><pre>{verification.prompt_preview}</pre></div>
                    )}
                    <h4>{t('dataSources.step5.usage', 'Quand l’utiliser')}</h4>
                    <div className="ds-also">
                        {[['enrich_on_ingest', t('dataSources.usage.ingest', 'À la réception de chaque ticket (recommandé)')], ['ai_tool', t('dataSources.usage.tool', 'Quand l’agent le demande pendant sa réponse')], ['scheduled_pull', t('dataSources.usage.scheduled', 'Toutes les 6 heures pour les tickets ouverts')]].map(([mode, label]) => (
                            <label key={mode} className="ds-check"><input type="checkbox" checked={spec.modes.includes(mode)} onChange={(e) => setSpec({ modes: e.target.checked ? [...spec.modes, mode] : spec.modes.filter((m) => m !== mode), schedule: mode === 'scheduled_pull' && e.target.checked ? { every_minutes: 360 } : spec.schedule })} /> {label}</label>
                        ))}
                    </div>
                    <p className="ds-muted">{t('dataSources.step5.workflowHint', 'L’ordre entre les sources, les conditions par canal ou par client et le comportement quand rien n’est trouvé se règlent dans « Parcours d’un ticket ».')}</p>
                </div>
            )}

            <div className="ds-wizard-foot">
                {step > 1 && <button type="button" className="ds-btn" onClick={() => setStep(step - 1)}><FaArrowLeft /> {t('dataSources.prev', 'Précédent')}</button>}
                {step < 5 && <button type="button" className="ds-btn ds-btn-primary" disabled={!canNext} onClick={() => setStep(step + 1)}>{t('dataSources.next', 'Suivant')} <FaArrowRight /></button>}
                {step === 5 && <button type="button" className="ds-btn ds-btn-primary" disabled={busy === 'save'} onClick={save}><FaSave /> {draft.saved ? t('dataSources.saveChanges', 'Enregistrer les modifications') : t('dataSources.activate', 'Enregistrer et activer')}</button>}
            </div>
          </div>
          <ConnectorAssistant draftId={draftId} draft={{ key: draft.key, base_url: draft.base_url, auth: draft.auth, spec }} lastTest={lastTest} onApply={applyFromAssistant}
              stepHint={t(`dataSources.steps.${STEPS[step - 1]}`, STEPS[step - 1])} />
        </div>
    );
};

const FieldRow = ({ t, label, expr, value, selected, onSelect, onClear }) => (
    <div className={`ds-fieldrow ${selected ? 'selected' : ''} ${expr ? 'mapped' : ''}`} onClick={onSelect} role="button" tabIndex={0}>
        <span className="ds-fieldrow-label">{label}</span>
        <span className="ds-fieldrow-value">{value !== undefined && value !== null ? String(value) : expr ? <em>{t('dataSources.step4.noValue', 'aucune valeur dans l’exemple')}</em> : <em>{t('dataSources.step4.unmapped', 'non affecté')}</em>}</span>
        {expr && <code className="ds-fieldrow-expr">{exprText(expr)}</code>}
        {onClear && <button type="button" className="ds-link" onClick={(e) => { e.stopPropagation(); onClear(); }}>×</button>}
    </div>
);

const ParamsEditor = ({ t, label, value, onChange }) => {
    const entries = Object.entries(value || {});
    const set = (index, k, v) => { const next = entries.map(([ek, ev], i) => (i === index ? [k, v] : [ek, ev])); onChange(Object.fromEntries(next.filter(([ek]) => ek))); };
    return (
        <div className="ds-params">
            <span className="ds-field-label">{label}</span>
            {entries.map(([k, v], i) => (
                <div key={i} className="ds-row">
                    <input className="ds-input ds-short ds-mono" value={k} onChange={(e) => set(i, e.target.value, v)} placeholder={t('dataSources.step3.key', 'clé')} />
                    <input className="ds-input ds-grow ds-mono" value={typeof v === 'string' ? v : JSON.stringify(v)} onChange={(e) => set(i, k, e.target.value)} placeholder="{order_number}" />
                    <button type="button" className="ds-link" onClick={() => onChange(Object.fromEntries(entries.filter((_, j) => j !== i)))}>×</button>
                </div>
            ))}
            <button type="button" className="ds-link" onClick={() => onChange({ ...(value || {}), '': '' })}><FaPlus /> {t('dataSources.step3.addParam', 'Ajouter')}</button>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Path B — web portal: show once how an order is found, the system re-reads it
// ---------------------------------------------------------------------------

const RECIPES = '/api/logistics/carrier-recipes';
const WEB_STEPS = ['name', 'connection', 'search', 'read', 'verify'];
const SEARCH_VARIABLES = ['order_number', 'tracking_number', 'customer_email', 'invoice_number', 'client_ref', 'client_name'];
const LIST_SECTIONS = ['shipments', 'events', 'stock_lines'];

const WebWizard = ({ t, catalog, onBack, onDone, category: initialCategory = null }) => {
    const [step, setStep] = useState(1);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState(initialCategory);
    const [startUrl, setStartUrl] = useState('');
    const [session, setSession] = useState(null);
    const [frame, setFrame] = useState(null);
    const [frameKey, setFrameKey] = useState(0);
    const [frameSrc, setFrameSrc] = useState(null);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState('');
    const [value, setValue] = useState('');
    const [variable, setVariable] = useState('username');
    const [secret, setSecret] = useState(false);
    const [clientBound, setClientBound] = useState(false);
    const [field, setField] = useState({ section: 'order', name: 'status' });
    const [tablePick, setTablePick] = useState(null); // { t, section, columns: {index: field} }
    const [hints, setHints] = useState(null);
    const [recipe, setRecipe] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [identifiers, setIdentifiers] = useState({});
    const [account, setAccount] = useState({ username: '', password: '' });
    const [verifyDays, setVerifyDays] = useState(true);
    const [modes, setModes] = useState(['enrich_on_ingest']);
    const [busy, setBusy] = useState(null);
    const [message, setMessage] = useState(null);
    const sessionRef = useRef(null);
    const draftId = useRef(newDraftId());
    const fail = (e, fallback) => { const d = e?.response?.data?.detail; setMessage({ type: 'error', text: typeof d === 'string' ? d : fallback }); };
    const key = slug(title);
    const effitrace = catalog.find((e) => e.system === 'effitrace' && e.verification_available);

    useEffect(() => () => { if (sessionRef.current) axios.delete(`${RECIPES}/studio/sessions/${sessionRef.current}`, authHeaders()).catch(() => {}); }, []);

    // The frame is behind the same bearer token as the rest of the API: fetch it as a blob.
    useEffect(() => {
        if (!session || !frameKey) return undefined;
        let url = null;
        let cancelled = false;
        axios.get(`${RECIPES}/studio/sessions/${session.id}/screenshot`, { ...authHeaders(), responseType: 'blob' })
            .then((res) => { if (cancelled) return; url = URL.createObjectURL(res.data); setFrameSrc(url); })
            .catch(() => { if (!cancelled) setFrameSrc(null); });
        return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    }, [session, frameKey]);

    const showFrame = (data) => { setFrame(data.frame || data); setFrameKey((k) => k + 1); setSelected(null); setValue(''); setHints(null); };

    const openSession = async () => {
        setBusy('open');
        setMessage(null);
        try {
            const res = await axios.post(`${RECIPES}/studio/sessions`, { carrier: title || key, operation: 'lookup', start_url: startUrl }, authHeaders());
            setSession(res.data.session);
            sessionRef.current = res.data.session.id;
            showFrame(res.data);
        } catch (e) {
            fail(e, t('dataSources.web.openError', 'Impossible d’ouvrir le portail sur le serveur.'));
        } finally {
            setBusy(null);
        }
    };

    const act = async (action, extra = {}) => {
        if (!session) return;
        setBusy('act');
        setMessage(null);
        try {
            const res = await axios.post(`${RECIPES}/studio/sessions/${session.id}/act`, { action, k: selected?.k ?? null, value: value || null, ...extra }, authHeaders());
            showFrame(res.data);
            return res.data;
        } catch (e) {
            fail(e, t('dataSources.web.actError', 'L’action a échoué sur le portail.'));
            return null;
        } finally {
            setBusy(null);
        }
    };
    const undo = async () => { if (!session) return; setBusy('act'); try { const res = await axios.post(`${RECIPES}/studio/sessions/${session.id}/undo`, {}, authHeaders()); showFrame(res.data); } catch (e) { fail(e, t('dataSources.web.actError', 'L’action a échoué sur le portail.')); } finally { setBusy(null); } };

    const fillVariable = () => act('fill', { variable: step === 2 ? variable : (clientBound ? 'client_name' : variable), secret: step === 2 && secret });
    const selectVariable = () => act('select', { variable: clientBound ? 'client_name' : null });
    const extractField = async () => {
        const name = `${field.section}_${field.name}`;
        const res = await act('extract', { variable: name });
        if (res?.value !== undefined) setMessage({ type: 'ok', text: t('dataSources.web.extracted', '« {{value}} » lu comme {{field}}.', { value: res.value, field: t(`dataSources.field.${field.section}.${field.name}`, field.name) }) });
    };
    const extractTable = async () => {
        if (!tablePick) return;
        const columns = Object.fromEntries(Object.entries(tablePick.columns).filter(([, v]) => v));
        setSelected(null);
        const res = await act('extract_table', { k: tablePick.t, variable: tablePick.section, fields: columns });
        if (res) { setMessage({ type: 'ok', text: t('dataSources.web.tableExtracted', 'Tableau lu comme {{section}} ({{n}} colonnes).', { section: t(`connectorStudio.section_${tablePick.section}`, tablePick.section), n: Object.keys(columns).length }) }); setTablePick(null); }
    };
    const readPageHints = async () => {
        if (!frame?.text) return;
        setBusy('hints');
        try {
            const res = await axios.post(`${API}/assistant/read-page`, { text: frame.text }, authHeaders());
            setHints(res.data);
        } catch (e) {
            fail(e, t('assistant.explainError', 'L’explication a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const finish = async () => {
        if (!session) return;
        setBusy('finish');
        setMessage(null);
        try {
            const drafted = await axios.post(`${RECIPES}/studio/sessions/${session.id}/draft?review=false`, {}, authHeaders());
            const draft = { ...drafted.data.draft, description: `${title} — ${t('dataSources.web.recipeDescription', 'lecture de commande enregistrée dans le parcours site web')}` };
            const saved = await axios.post(`${RECIPES}/drafts`, { recipe: draft }, authHeaders());
            setRecipe(saved.data.recipe);
            setAccount((a) => ({ ...a, username: a.username || frame?.variables?.username || '' }));
            await axios.delete(`${RECIPES}/studio/sessions/${session.id}`, authHeaders()).catch(() => {});
            sessionRef.current = null;
            setSession(null);
            setStep(5);
        } catch (e) {
            fail(e, t('dataSources.web.finishError', 'Le parcours n’a pas pu être enregistré.'));
        } finally {
            setBusy(null);
        }
    };

    const runTest = async () => {
        if (!recipe) return;
        setBusy('test');
        setMessage(null);
        setTestResult(null);
        try {
            const variables = { ...identifiers, username: account.username, password: account.password };
            const res = await axios.post(`${RECIPES}/${encodeURIComponent(recipe.db_id || recipe.id)}/test-run`, { variables }, authHeaders());
            setTestResult(res.data);
            if (res.data.recipe) setRecipe(res.data.recipe);
        } catch (e) {
            fail(e, t('dataSources.readError', 'La lecture a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const buildSpec = () => {
        const extracted = testResult?.run?.extracted || recipe?.extracted_sample || {};
        const mapping = {};
        Object.keys(extracted).forEach((name) => {
            if (LIST_SECTIONS.includes(name)) {
                const first = Array.isArray(extracted[name]) && extracted[name][0] ? extracted[name][0] : {};
                mapping[name] = { each: `$.${name}`, fields: Object.fromEntries(Object.keys(first).map((f) => [f, `$.${f}`])) };
                return;
            }
            const meta = FIELD_SECTIONS.find((s) => !s.list && name.startsWith(`${s.section}_`));
            if (!meta) return;
            const fieldName = name.slice(meta.section.length + 1);
            mapping[meta.section] = { ...(mapping[meta.section] || {}), [fieldName]: `$.${name}` };
        });
        const variables = {};
        (recipe?.variables || []).forEach((v) => { if (SEARCH_VARIABLES.includes(v)) variables[v] = `{${v}}`; });
        const inputs = Object.keys(variables);
        const spec = {
            spec_version: 1, title, transport: 'browser_recipe', inputs, require_any: inputs.filter((i) => i !== 'client_name'), params: {},
            steps: [], recipe: { id: recipe.id, variables, not_found_when: { empty: `$.${Object.keys(mapping.order || {}).length ? `order_${Object.keys(mapping.order)[0]}` : Object.keys(extracted)[0] || 'order_status'}` } },
            mapping, modes, operations: { timeout_s: 180, retries: 0, health_alerts: true }, presentation: { path: 'web', draft_id: draftId.current, recipe_id: recipe.id },
            ...(category ? { category } : {}),
        };
        if (modes.includes('ai_tool')) spec.tool = { name: `lookup_${key}`, description: `Look up an order in ${title}` };
        if (modes.includes('scheduled_pull')) spec.schedule = { every_minutes: 360 };
        if (effitrace && verifyDays) { spec.shadow_of = 'effitrace'; spec.verification = { days: 14 }; }
        return spec;
    };

    const activate = async () => {
        if (!recipe || !key) return;
        setBusy('save');
        setMessage(null);
        try {
            await axios.post(`${RECIPES}/${encodeURIComponent(recipe.db_id || recipe.id)}/status`, { status: 'published' }, authHeaders());
            const spec = buildSpec();
            await axios.put(`${API}/${encodeURIComponent(key)}`, { spec, base_url: startUrl, auth: { type: 'portal', username: account.username, password: account.password }, enabled: true }, authHeaders());
            onDone(key);
        } catch (e) {
            fail(e, t('dataSources.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const elements = (frame?.elements || []).filter((e) => !filter || JSON.stringify(e).toLowerCase().includes(filter.toLowerCase()));
    const canPublish = recipe && account.username && account.password && ['success', 'stopped_before_submit'].includes(testResult?.run?.status || recipe?.last_test?.status);

    const recorder = (
        <div className="ds-recorder">
            <div className="ds-recorder-frame">
                <div className="ds-muted ds-mono">{frame?.url}</div>
                {frameSrc ? <img src={frameSrc} alt={t('dataSources.web.frameAlt', 'Page du portail')} /> : <div className="ds-frame-empty">{t('dataSources.web.noFrame', 'Aucune image de la page pour le moment.')}</div>}
                <div className="ds-row">
                    <span className="ds-muted">{t('dataSources.web.recorded', '{{n}} actions enregistrées', { n: frame?.steps ?? 0 })}</span>
                    <button type="button" className="ds-btn" disabled={busy === 'act'} onClick={undo}>{t('dataSources.web.undo', 'Annuler la dernière')}</button>
                </div>
            </div>
            <div className="ds-recorder-side">
                <input className="ds-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('dataSources.web.filter', 'Filtrer les éléments de la page')} />
                <div className="ds-elements">
                    {elements.map((e) => (
                        <button type="button" key={e.k} className={`ds-el ${selected?.k === e.k ? 'selected' : ''}`} onClick={() => { setSelected(e); setTablePick(null); }}>
                            <span className="ds-el-role">{e.role || e.tag}</span> <span className="ds-el-label">{e.label || e.placeholder || e.text || e.id || e.css}</span>
                        </button>
                    ))}
                </div>
                {selected && (
                    <div className="ds-el-actions">
                        <div className="ds-muted">{selected.label || selected.text || selected.css}</div>
                        {step !== 4 && (
                            <>
                                {['textbox', 'combobox', null].includes(selected.role) && selected.tag !== 'select' && (
                                    <div className="ds-row">
                                        <input className="ds-input ds-grow" value={value} onChange={(e) => setValue(e.target.value)} placeholder={t('dataSources.web.typedValue', 'Valeur à saisir')} type={secret ? 'password' : 'text'} />
                                        {step === 2 ? (
                                            <select className="ds-input ds-short" value={variable} onChange={(e) => { setVariable(e.target.value); setSecret(e.target.value === 'password'); }}>
                                                <option value="username">{t('dataSources.web.varUsername', 'identifiant du compte')}</option>
                                                <option value="password">{t('dataSources.web.varPassword', 'mot de passe (non conservé ; redemandé à la fin)')}</option>
                                                <option value="">{t('dataSources.web.varNone', 'valeur fixe')}</option>
                                            </select>
                                        ) : (
                                            <select className="ds-input ds-short" value={clientBound ? 'client_name' : variable} onChange={(e) => { setClientBound(e.target.value === 'client_name'); setVariable(e.target.value); }}>
                                                {SEARCH_VARIABLES.map((v) => <option key={v} value={v}>{t(`ticketWorkflow.identifier.${v}`, v)}</option>)}
                                                <option value="">{t('dataSources.web.varNone', 'valeur fixe')}</option>
                                            </select>
                                        )}
                                        <button type="button" className="ds-btn" disabled={busy === 'act'} onClick={fillVariable}>{t('dataSources.web.fill', 'Saisir')}</button>
                                    </div>
                                )}
                                {(selected.tag === 'select' || selected.role === 'combobox') && (
                                    <div className="ds-row">
                                        <select className="ds-input ds-grow" value={value} onChange={(e) => setValue(e.target.value)}>
                                            <option value="">{t('dataSources.web.chooseOption', 'Choisir une option…')}</option>
                                            {(selected.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                        {step === 3 && <label className="ds-check"><input type="checkbox" checked={clientBound} onChange={(e) => setClientBound(e.target.checked)} /> {t('dataSources.web.dependsOnClient', 'change selon le client')}</label>}
                                        <button type="button" className="ds-btn" disabled={busy === 'act' || !value} onClick={selectVariable}>{t('dataSources.web.select', 'Choisir')}</button>
                                    </div>
                                )}
                                <button type="button" className="ds-btn" disabled={busy === 'act'} onClick={() => act('click')}>{t('dataSources.web.click', 'Cliquer')}</button>
                            </>
                        )}
                        {step === 4 && (
                            <div className="ds-row">
                                <span>{t('dataSources.web.whatIsIt', 'C’est quoi ?')}</span>
                                <select className="ds-input ds-grow" value={`${field.section}.${field.name}`} onChange={(e) => { const [section, name] = e.target.value.split('.'); setField({ section, name }); }}>
                                    {FIELD_SECTIONS.filter((s) => !s.list).map((s) => s.fields.map((f) => <option key={`${s.section}.${f}`} value={`${s.section}.${f}`}>{t(`connectorStudio.section_${s.section}`, s.section)} · {t(`dataSources.field.${s.section}.${f}`, f)}</option>))}
                                </select>
                                <button type="button" className="ds-btn ds-btn-primary" disabled={busy === 'act'} onClick={extractField}>{t('dataSources.web.read', 'Lire cette valeur')}</button>
                                <button type="button" className="ds-btn" disabled={busy === 'act'} onClick={() => act('click')}>{t('dataSources.web.click', 'Cliquer')}</button>
                            </div>
                        )}
                    </div>
                )}
                {step === 4 && (frame?.tables || []).length > 0 && (
                    <div className="ds-tables">
                        <div className="ds-muted">{t('dataSources.web.tables', 'Tableaux de la page')}</div>
                        {frame.tables.map((tb) => (
                            <button type="button" key={tb.t} className={`ds-el ${tablePick?.t === tb.t ? 'selected' : ''}`} onClick={() => { setSelected(null); setTablePick({ t: tb.t, section: 'shipments', columns: {} }); }}>
                                <span className="ds-el-role">table</span> <span className="ds-el-label">{(tb.headers || []).filter(Boolean).slice(0, 5).join(' · ') || tb.css} ({tb.rows})</span>
                            </button>
                        ))}
                        {tablePick && (() => { const tb = frame.tables.find((x) => x.t === tablePick.t); return tb && (
                            <div className="ds-el-actions">
                                <select className="ds-input" value={tablePick.section} onChange={(e) => setTablePick({ ...tablePick, section: e.target.value })}>
                                    {LIST_SECTIONS.map((s) => <option key={s} value={s}>{t(`connectorStudio.section_${s}`, s)}</option>)}
                                </select>
                                {(tb.headers || []).map((h, i) => (
                                    <div key={i} className="ds-row">
                                        <span className="ds-muted ds-short">{h || `#${i + 1}`}{tb.sample?.[i] ? ` — ${tb.sample[i]}` : ''}</span>
                                        <select className="ds-input ds-grow" value={tablePick.columns[i] || ''} onChange={(e) => setTablePick({ ...tablePick, columns: { ...tablePick.columns, [i]: e.target.value } })}>
                                            <option value="">{t('dataSources.web.ignoreColumn', '(ignorer)')}</option>
                                            {FIELD_SECTIONS.find((s) => s.section === tablePick.section).fields.map((f) => <option key={f} value={f}>{t(`dataSources.field.${tablePick.section}.${f}`, f)}</option>)}
                                        </select>
                                    </div>
                                ))}
                                <button type="button" className="ds-btn ds-btn-primary" disabled={busy === 'act' || !Object.values(tablePick.columns).some(Boolean)} onClick={extractTable}>{t('dataSources.web.readTable', 'Lire ce tableau')}</button>
                            </div>
                        ); })()}
                    </div>
                )}
                {step === 4 && (
                    <div className="ds-hints">
                        <button type="button" className="ds-btn" disabled={busy === 'hints' || !frame?.text} onClick={readPageHints}><FaMagic /> {t('dataSources.web.suggest', 'Que voit l’assistant sur cette page ?')}</button>
                        {hints && Object.keys(hints.fields || {}).length > 0 && (
                            <ul className="ds-hint-list">{Object.entries(hints.fields).map(([k, v]) => <li key={k}><strong>{k}</strong> : {v}</li>)}</ul>
                        )}
                        {hints && Object.keys(hints.fields || {}).length === 0 && <p className="ds-muted">{t('dataSources.web.noHints', 'Aucune valeur reconnue sur cette page.')}</p>}
                    </div>
                )}
                {frame?.extracted && Object.keys(frame.extracted).length > 0 && (
                    <div className="ds-extracted">
                        <div className="ds-muted">{t('dataSources.web.readSoFar', 'Lu jusqu’ici')}</div>
                        {Object.entries(frame.extracted).map(([k, v]) => <div key={k}><code>{k}</code> : {Array.isArray(v) ? `${v.length} ${t('dataSources.step4.items', 'éléments')}` : String(v)}</div>)}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="ds-card">
            <div className="ds-wizard-head">
                <button type="button" className="ds-link" onClick={onBack}><FaArrowLeft /> {t('dataSources.back', 'Retour')}</button>
                <ol className="ds-stepper">{WEB_STEPS.map((name, i) => <li key={name} className={step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}><span>{i + 1}</span> {t(`dataSources.webSteps.${name}`, name)}</li>)}</ol>
            </div>
            {message && <div className={`ds-alert ${message.type === 'ok' ? 'ds-alert-ok' : 'ds-alert-error'}`}>{message.type === 'ok' ? <FaCheckCircle /> : <FaTimesCircle />} {message.text}</div>}

            {step === 1 && (
                <div className="ds-step">
                    <h2>{t('dataSources.step1.title', 'Nom et documentation')}</h2>
                    <label className="ds-field"><span>{t('dataSources.step1.name', 'Nom de la source')}</span><input className="ds-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('dataSources.web.namePlaceholder', 'ex. Extranet WMS Acme')} /></label>
                    <CategorySelect t={t} value={category} onChange={setCategory} />
                    <label className="ds-field"><span>{t('dataSources.web.startUrl', 'Adresse de la page de connexion du portail')}</span><input className="ds-input" value={startUrl} onChange={(e) => setStartUrl(e.target.value)} placeholder="https://extranet.mon-wms.com/login" /></label>
                    <p className="ds-muted">{t('dataSources.web.intro', 'Le navigateur tourne sur le serveur, avec l’adresse IP que le portail connaît. Vous montrez une fois comment vous vous connectez, cherchez une commande et lisez ses informations ; rien n’est modifié sur le portail.')}</p>
                </div>
            )}
            {step === 2 && (
                <div className="ds-step">
                    <h2>{t('dataSources.web.step2', 'Connexion')}</h2>
                    <p className="ds-muted">{t('dataSources.web.step2Hint', 'Choisissez le champ identifiant dans la liste, saisissez-le ; puis le mot de passe (il reste dans le navigateur du serveur, jamais dans la source) ; puis cliquez le bouton de connexion.')}</p>
                    {!session ? <button type="button" className="ds-btn ds-btn-primary" disabled={busy === 'open' || !startUrl} onClick={openSession}><FaGlobe /> {busy === 'open' ? t('dataSources.web.opening', 'Ouverture…') : t('dataSources.web.open', 'Ouvrir le portail')}</button> : recorder}
                </div>
            )}
            {step === 3 && session && (
                <div className="ds-step">
                    <h2>{t('dataSources.web.step3', 'Trouver une commande')}</h2>
                    <p className="ds-muted">{t('dataSources.web.step3Hint', 'Cherchez une commande comme vous le faites d’habitude. Pour chaque valeur saisie, dites de quel identifiant il s’agit ; une liste qui change selon le client est liée à la fiche client.')}</p>
                    {recorder}
                </div>
            )}
            {step === 4 && session && (
                <div className="ds-step">
                    <h2>{t('dataSources.web.step4', 'Lire la commande')}</h2>
                    <p className="ds-muted">{t('dataSources.web.step4Hint', 'Sur la page de la commande, sélectionnez une information dans la liste et dites ce que c’est. Un tableau (colis, événements, lignes) se lit en une fois en nommant ses colonnes.')}</p>
                    {recorder}
                </div>
            )}
            {step === 5 && (
                <div className="ds-step">
                    <h2>{t('dataSources.step5.title', 'Vérification et usage')}</h2>
                    {recipe && <p className="ds-muted">{t('dataSources.web.recipeSaved', 'Parcours enregistré ({{n}} actions, {{v}} variables). Vérifiez-le avec une vraie commande, puis activez la source.', { n: (recipe.steps || []).length, v: (recipe.variables || []).length })}</p>}
                    <h4>{t('dataSources.web.account', 'Compte utilisé par le système')}</h4>
                    <p className="ds-muted">{t('dataSources.web.accountHint', 'Le mot de passe saisi pendant l’enregistrement n’a pas été conservé. Celui-ci est chiffré et sert uniquement aux lectures.')}</p>
                    <div className="ds-row">
                        <input className="ds-input ds-short" autoComplete="off" value={account.username} placeholder={t('dataSources.web.varUsername', 'identifiant du compte')} onChange={(e) => setAccount({ ...account, username: e.target.value })} />
                        <input className="ds-input ds-short" type="password" autoComplete="new-password" value={account.password} placeholder="••••••••" onChange={(e) => setAccount({ ...account, password: e.target.value })} />
                    </div>
                    <h4>{t('dataSources.step5.check', 'Vérifier avec une commande')}</h4>
                    <div className="ds-row">
                        {(recipe?.variables || []).filter((v) => SEARCH_VARIABLES.includes(v)).map((v) => <input key={v} className="ds-input ds-short" placeholder={t(`ticketWorkflow.identifier.${v}`, v)} value={identifiers[v] || ''} onChange={(e) => setIdentifiers({ ...identifiers, [v]: e.target.value })} />)}
                        <button type="button" className="ds-btn" disabled={busy === 'test' || !recipe || !account.username || !account.password} onClick={runTest}><FaPlay /> {t('dataSources.step5.verify', 'Vérifier avec cette commande')}</button>
                    </div>
                    {testResult?.run && (
                        <div className={`ds-alert ${testResult.run.status === 'success' ? 'ds-alert-ok' : 'ds-alert-error'}`}>
                            {testResult.run.status === 'success' ? <FaCheckCircle /> : <FaTimesCircle />} {testResult.run.status === 'success' ? t('dataSources.web.testOk', 'Lecture réussie en {{ms}} ms : {{n}} valeurs.', { ms: testResult.run.duration_ms, n: Object.keys(testResult.run.extracted || {}).length }) : `${testResult.run.failure_category || testResult.run.status}${testResult.run.error ? ` — ${testResult.run.error}` : ''} (${testResult.run.reached_step})`}
                        </div>
                    )}
                    {testResult?.run?.extracted && <pre className="ds-preview-pre">{JSON.stringify(testResult.run.extracted, null, 2)}</pre>}
                    <h4>{t('dataSources.step5.usage', 'Quand l’utiliser')}</h4>
                    <div className="ds-also">
                        {[['enrich_on_ingest', t('dataSources.usage.ingest', 'À la réception de chaque ticket (recommandé)')], ['ai_tool', t('dataSources.usage.tool', 'Quand l’agent le demande pendant sa réponse')], ['scheduled_pull', t('dataSources.usage.scheduled', 'Toutes les 6 heures pour les tickets ouverts')]].map(([mode, label]) => (
                            <label key={mode} className="ds-check"><input type="checkbox" checked={modes.includes(mode)} onChange={(e) => setModes(e.target.checked ? [...modes, mode] : modes.filter((m) => m !== mode))} /> {label}</label>
                        ))}
                        {effitrace && <label className="ds-check"><input type="checkbox" checked={verifyDays} onChange={(e) => setVerifyDays(e.target.checked)} /> {t('dataSources.catalog.verify', 'Comparer pendant 14 jours avant de remplacer')} <span className="ds-muted">— {effitrace.verification_hint}</span></label>}
                    </div>
                </div>
            )}

            <div className="ds-wizard-foot">
                {step > 1 && step < 5 && <button type="button" className="ds-btn" onClick={() => setStep(step - 1)}><FaArrowLeft /> {t('dataSources.prev', 'Précédent')}</button>}
                {step === 1 && <button type="button" className="ds-btn ds-btn-primary" disabled={!title || !/^https?:\/\//.test(startUrl)} onClick={() => setStep(2)}>{t('dataSources.next', 'Suivant')} <FaArrowRight /></button>}
                {(step === 2 || step === 3) && <button type="button" className="ds-btn ds-btn-primary" disabled={!session} onClick={() => setStep(step + 1)}>{t('dataSources.next', 'Suivant')} <FaArrowRight /></button>}
                {step === 4 && <button type="button" className="ds-btn ds-btn-primary" disabled={busy === 'finish' || !frame?.extracted || Object.keys(frame.extracted).length === 0} onClick={finish}>{t('dataSources.web.finish', 'Terminer le parcours')} <FaArrowRight /></button>}
                {step === 5 && <button type="button" className="ds-btn ds-btn-primary" disabled={busy === 'save' || !canPublish} onClick={activate}><FaSave /> {t('dataSources.activate', 'Enregistrer et activer')}</button>}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Path C — catalogue
// ---------------------------------------------------------------------------

const CatalogInstall = ({ t, entries, onBack, onDone }) => {
    const [entry, setEntry] = useState(null);
    const [fields, setFields] = useState({});
    const [verify, setVerify] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const choose = (e) => { setEntry(e); setFields(Object.fromEntries((e.fields || []).map((f) => [f.name, f.default || '']))); setError(null); };
    const install = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await axios.post(`${API}/catalog/${entry.system}/install`, { fields, brand_id: entry.brand_id || null, verify_days: entry.verification_available && verify ? 14 : null }, authHeaders());
            onDone(res.data.key);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === 'string' ? d : t('dataSources.installError', 'L’installation a échoué.'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="ds-card">
            <button type="button" className="ds-link" onClick={entry ? () => setEntry(null) : onBack}><FaArrowLeft /> {t('dataSources.back', 'Retour')}</button>
            {!entry && (
                <>
                    <h2>{t('dataSources.catalog.title', 'Systèmes connus')}</h2>
                    <div className="ds-choices">
                        {entries.map((e) => (
                            <button type="button" key={`${e.system}:${e.brand_id || ''}`} className="ds-choice" onClick={() => choose(e)}>
                                <strong>{e.title}</strong><span>{e.description}</span>
                                {e.installed && <span className="ds-pill ds-pill-ok">{t('dataSources.catalog.installed', 'déjà installé — réinstaller met à jour')}</span>}
                            </button>
                        ))}
                    </div>
                </>
            )}
            {entry && (
                <div className="ds-step">
                    <h2>{entry.title}</h2>
                    <p className="ds-muted">{entry.description}</p>
                    {entry.fields.map((f) => (
                        <label key={f.name} className="ds-field"><span>{f.label}{f.required ? ' *' : ''}</span>
                            <input className="ds-input" type={f.type === 'secret' ? 'password' : 'text'} value={fields[f.name] || ''} placeholder={f.type === 'secret' ? '••••••••' : ''} onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })} /></label>
                    ))}
                    {entry.verification_available && (
                        <label className="ds-check"><input type="checkbox" checked={verify} onChange={(e) => setVerify(e.target.checked)} /> {t('dataSources.catalog.verify', 'Comparer pendant 14 jours avant de remplacer')} <span className="ds-muted">— {entry.verification_hint}</span></label>
                    )}
                    {error && <div className="ds-alert ds-alert-error"><FaTimesCircle /> {error}</div>}
                    <div className="ds-wizard-foot">
                        <button type="button" className="ds-btn ds-btn-primary" disabled={busy || entry.fields.some((f) => f.required && !fields[f.name])} onClick={install}><FaSave /> {t('dataSources.catalog.install', 'Installer')}</button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// A source's page
// ---------------------------------------------------------------------------

const SourcePage = ({ t, keyName, onBack, onDeleted }) => {
    const [doc, setDoc] = useState(null);
    const [tab, setTab] = useState('steps');
    const [history, setHistory] = useState(null);
    const [shadow, setShadow] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/${encodeURIComponent(keyName)}`, authHeaders());
            setDoc(res.data);
        } catch (e) {
            setError(t('dataSources.loadError', 'Impossible de charger les sources de données.'));
        }
    }, [keyName, t]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (tab === 'history') axios.get(`${API}/${encodeURIComponent(keyName)}/history?limit=50`, authHeaders()).then((r) => setHistory(r.data)).catch(() => setHistory(null));
        if (tab === 'verification') axios.get(`${API}/${encodeURIComponent(keyName)}/shadow-runs?limit=50`, authHeaders()).then((r) => setShadow(r.data)).catch(() => setShadow(null));
    }, [tab, keyName]);

    const remove = async () => {
        try { await axios.delete(`${API}/${encodeURIComponent(keyName)}`, authHeaders()); onDeleted(); } catch (e) { setError(t('dataSources.deleteError', 'Suppression impossible.')); }
    };
    const goLive = async () => {
        try {
            const spec = { ...doc.spec };
            delete spec.shadow_of;
            await axios.put(`${API}/${encodeURIComponent(keyName)}`, { spec, base_url: doc.base_url, auth: doc.auth, enabled: true }, authHeaders());
            await load();
        } catch (e) { setError(t('dataSources.saveError', 'Enregistrement impossible.')); }
    };

    if (!doc) return <div className="ds-card"><p className="ds-muted">{error || t('dataSources.loading', 'Chargement…')}</p></div>;
    const guided = doc.spec?.transport === 'http_json' && (doc.spec?.steps || []).length === 1 && !doc.brand_id && doc.spec?.presentation?.path !== 'catalog';
    const initial = guided ? { key: doc.key, base_url: doc.base_url || '', auth: doc.auth || { type: 'none' }, enabled: doc.enabled !== false, spec: doc.spec, sample: null, saved: true } : null;
    return (
        <div className="ds-source">
            <div className="ds-source-head">
                <button type="button" className="ds-link" onClick={onBack}><FaArrowLeft /> {t('dataSources.back', 'Retour')}</button>
                <h2>{doc.title || doc.key} <span className="ds-muted ds-mono">{doc.key}</span></h2>
                <span className={`ds-pill ds-pill-${stateOf(doc, t).cls}`}>{stateOf(doc, t).label}</span>
                {doc.shadow_of && <button type="button" className="ds-btn ds-btn-primary" onClick={goLive}>{t('dataSources.goLive', 'Remplacer maintenant')}</button>}
                <button type="button" className="ds-btn ds-btn-danger" onClick={remove}><FaTrash /></button>
            </div>
            {error && <div className="ds-alert ds-alert-error"><FaTimesCircle /> {error}</div>}
            <div className="ds-tabs">
                {[['steps', FaListUl, t('dataSources.tabs.steps', 'Réglages')], ['history', FaHistory, t('dataSources.tabs.history', 'Historique')], ['verification', FaEye, t('dataSources.tabs.verification', 'Vérification')], ['expert', FaCode, t('dataSources.tabs.expert', 'Mode expert')]].map(([name, Icon, label]) => (
                    <button type="button" key={name} className={tab === name ? 'active' : ''} onClick={() => setTab(name)}><Icon /> {label}</button>
                ))}
            </div>
            {tab === 'steps' && (guided
                ? <ApiWizard t={t} initial={initial} onBack={onBack} onDone={() => {}} onSaved={() => load()} />
                : (
                    <div className="ds-card">
                        <p className="ds-muted">{t('dataSources.notGuided', 'Cette source vient du catalogue ou d’une conversion : ses réglages s’éditent en mode expert. Les identifiants de connexion se modifient en réinstallant l’entrée du catalogue.')}</p>
                        <dl className="ds-dl">
                            <dt>{t('dataSources.system.label', 'Système')}</dt><dd>{systemLabel(doc, t)}</dd>
                            {doc.category && <><dt>{t('dataSources.step1.category', 'Type de système')}</dt><dd>{t(`dataSources.category.${doc.category}.title`, (CATEGORIES.find((c) => c.key === doc.category) || {}).title || doc.category)}</dd></>}
                            <dt>{t('dataSources.col.state', 'État')}</dt><dd>{stateOf(doc, t).label}</dd>
                            {doc.base_url && <><dt>{t('dataSources.step2.baseUrl', 'Adresse de l’API')}</dt><dd className="ds-mono">{doc.base_url}</dd></>}
                            <dt>{t('dataSources.usageLabel', 'Utilisation')}</dt><dd>{(doc.modes || []).map((m) => t(`ticketWorkflow.mode_${m}`, m)).join(', ') || '—'}</dd>
                        </dl>
                    </div>
                ))}
            {tab === 'history' && (
                <div className="ds-card">
                    {history?.stats && <p className="ds-muted">{t('dataSources.history.stats', '{{found}} commandes trouvées sur {{total}} lectures en 7 jours, {{failed}} échecs.', { found: history.stats.found_7d, total: history.stats.reads_7d, failed: history.stats.failed_7d })}</p>}
                    <div className="ds-table-wrap">
                        <table className="ds-table">
                            <thead><tr><th>{t('dataSources.history.when', 'Quand')}</th><th>{t('dataSources.history.identifiers', 'Identifiants')}</th><th>{t('dataSources.history.result', 'Résultat')}</th><th>{t('dataSources.history.duration', 'Durée')}</th></tr></thead>
                            <tbody>
                                {(history?.runs || []).length === 0 && <tr><td colSpan={4} className="ds-muted">{t('dataSources.history.empty', 'Aucune lecture encore.')}</td></tr>}
                                {(history?.runs || []).map((run) => (
                                    <tr key={run._id}>
                                        <td>{run.created_at ? new Date(run.created_at).toLocaleString() : ''}</td>
                                        <td className="ds-mono">{Object.entries(run.identifiers || {}).map(([k, v]) => `${k}=${v}`).join(' ')}</td>
                                        <td>{run.found ? <span className="ds-ok"><FaCheckCircle /> {t('dataSources.history.found', 'trouvé')}</span> : <span className={run.reason === 'not_found' ? 'ds-warn' : 'ds-bad'}>{run.explanation}</span>}</td>
                                        <td>{run.duration_ms ? `${run.duration_ms} ms` : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {tab === 'verification' && (
                <div className="ds-card">
                    {!doc.shadow_of && <p className="ds-muted">{t('dataSources.verification.none', 'Cette source n’est pas en période de vérification : elle enrichit les tickets.')}</p>}
                    {doc.shadow_of && shadow && (
                        <>
                            <p>{t('dataSources.verification.summary', 'Sur {{total}} comparaisons : {{agree}} résultats identiques, {{both}} trouvés des deux côtés, {{specOnly}} trouvés seulement par cette source, {{legacyOnly}} seulement par le mécanisme actuel, {{failures}} échecs.', { total: shadow.summary.total, agree: shadow.summary.agree, both: shadow.summary.both_found, specOnly: shadow.summary.spec_only, legacyOnly: shadow.summary.legacy_only, failures: shadow.summary.failures })}</p>
                            <div className="ds-table-wrap">
                                <table className="ds-table">
                                    <thead><tr><th>{t('dataSources.history.when', 'Quand')}</th><th>{t('dataSources.history.identifiers', 'Identifiants')}</th><th>{t('dataSources.verification.current', 'Mécanisme actuel')}</th><th>{t('dataSources.verification.new', 'Cette source')}</th><th>{t('dataSources.verification.agreement', 'Accord')}</th></tr></thead>
                                    <tbody>
                                        {shadow.runs.map((r) => (
                                            <tr key={r._id}>
                                                <td>{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</td>
                                                <td className="ds-mono">{Object.entries(r.identifiers || {}).map(([k, v]) => `${k}=${v}`).join(' ')}</td>
                                                <td>{r.legacy_found ? `${r.legacy_summary?.carrier || ''} ${r.legacy_summary?.status || ''}` : '—'}</td>
                                                <td>{r.spec_found ? `${r.spec_summary?.carrier || ''} ${r.spec_summary?.status || ''}` : (r.reason || '—')}</td>
                                                <td>{r.agreement?.agree ? <span className="ds-ok"><FaCheckCircle /></span> : <span className="ds-bad">{Object.entries(r.agreement || {}).filter(([k, v]) => v === false && k !== 'agree').map(([k]) => t(`dataSources.verification.field.${k}`, k)).join(', ') || '—'}</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
            {tab === 'expert' && <ConnectorStudio embedded initialKey={keyName} onChanged={load} />}
        </div>
    );
};

export default DataSources;
